import { BuilderComponent } from "@builder.io/react";
import { initializeBuilder } from "@faire/builder-core/BuilderComponents/initializeBuilder";
import { LoadingSkeleton } from "@faire/web/slate-deprecated/LoadingSkeleton";
import { useDoActionWhenVisible } from "@faire/web/ui/hooks/useDoActionWhenVisible";
import { useMergedRefs } from "@faire/web/ui/hooks/useMergedRefs";
import React, { Suspense, use, useEffect } from "react";

import { getBuilderAPIKey } from "../builderUtils";
import {
  BuilderOnClientProps,
  RenderWithBuilderProps,
  BuilderOnSSRProps,
} from "../types";

import { useBuilderContentQuery } from "./useBuilderContentQuery";

export const RenderWithBuilderInternal: React.FC<RenderWithBuilderProps> = (
  props
) => {
  if (props.isSSR) {
    return (
      <Suspense fallback={props.suspenseFallback}>
        {/* carousel and other widets are lazy loaded, so we need suspense  */}
        <RenderWithBuilderSSRBase {...props} />
      </Suspense>
    );
  }

  return (
    <Suspense fallback={null}>
      <RenderWithBuilderClientBase {...props} />
    </Suspense>
  );
};

const useTrackingRef = (
  trackingData: Pick<RenderWithBuilderProps, "loadTimingRef" | "onViewEvent">
) => {
  const onViewEventRef = useDoActionWhenVisible(trackingData.onViewEvent);
  const ref = useMergedRefs(trackingData.loadTimingRef, onViewEventRef);

  return ref;
};

const RenderWithBuilderSSRBase: React.FC<BuilderOnSSRProps> = ({
  builderIOApiKey,
  modelName,
  builderPromise,
  data,
  onViewEvent,
  loadTimingRef,
  contentLoadedCallback,
  context,
  registerCustomComponents,
}) => {
  //reinitialize builder on client side to make preview work
  initializeBuilder(builderIOApiKey, registerCustomComponents);

  const trackingRef = useTrackingRef({ loadTimingRef, onViewEvent });
  const builderContent = use(builderPromise);

  return (
    <div ref={trackingRef}>
      <BuilderComponent
        content={builderContent}
        model={modelName}
        data={data}
        context={context}
        contentLoaded={contentLoadedCallback}
      />
    </div>
  );
};

const RenderWithBuilderClientBase: React.FC<BuilderOnClientProps> = ({
  modelName,
  onViewEvent,
  loadTimingRef,
  data,
  targets,
  contentOptions,
  fullWidth,
  context,
  contentLoadedCallback,
  registerCustomComponents,
}) => {
  const apiKey = getBuilderAPIKey();

  useEffect(() => {
    initializeBuilder(apiKey, registerCustomComponents);
  }, [apiKey]);

  const { data: sectionContent, isPending } = useBuilderContentQuery(
    modelName,
    {
      userAttributes: targets,
      ...contentOptions,
    }
  );
  const trackingRef = useTrackingRef({ loadTimingRef, onViewEvent });

  if (isPending) {
    return <LoadingSkeleton aspectRatio="16/9" width="100%" />;
  }
  return (
    <div
      ref={trackingRef}
      className="builder-io-content"
      style={fullWidth ? { width: "100%" } : undefined}
    >
      <BuilderComponent
        content={sectionContent}
        model={modelName}
        data={data}
        context={context}
        contentLoaded={contentLoadedCallback}
      />
    </div>
  );
};
